import { ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { LaneManagerService } from '../lane-buttons/shared/services/lane-manager/lane-manager.service';
import { WorkItemLaneType } from '../lane-buttons/shared/services/lane-manager/work-item-lane-type.enum';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { TranslateService } from '@ngx-translate/core';
import { DiscussionRequestStates } from './discussion-request-states';
import { DiscussionErrors, DiscussionOperatorTypes } from './discussion-properties';
import { DiscussionsMessagesService } from './discussions-messages/discussions-messages.service';
import { InitDiscussionResponseModel } from 'common/api/models/init-discussion-response.model';
import { DynamicQueryAdapterService } from 'common/dynamicquery/dynamic-query-adapter.service';
import { DynamicQueryDecoratorService } from 'common/dynamicquery/dynamic-query-decorator.service';
import { DiscussionItem } from 'common/workitems/interfaces/discussion-item.interface';
import { BcRestApi } from 'common/api/rest-api';
import { Subscription } from 'rxjs';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { LinkTargetBlankFilter } from 'common/html-helper/link-target-blank';
import { WorkItemStateHandler } from '../../services/workitem-state-handler/workitem-state-handler.service';
import { EditorMessages } from '../editor-messages/editor-messages';
import { AssigneeTypes } from 'common/common/constants/boldchat-enums';
import { BcSettingsService } from 'common/settings/settings.service';
import { Settings } from 'common/settings/settings.interface';
import { BcFeatures } from 'common/common/constants/feature-enums';
import { OperatorsService } from 'common/operators/operators.service';
import { WorkItemHelper } from 'common/workitems/workitem-helper';
import { InnerHtmlPipe } from 'shared/components/canned-message-panel/filters/inner-html.pipe';

@Component({
  selector: 'app-discussions',
  templateUrl: './discussions.component.html',
  styleUrls: ['./discussions.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DiscussionsComponent implements OnInit, OnDestroy {
  @Input()
  workItemId: string;

  @Input()
  workItemType: string;

  @Input()
  helperDiscussionId: string;

  @Input()
  workItemIsEnded: boolean;

  @Input()
  isTransferButtonDisabled: boolean;

  @ViewChild('scrollPane')
  scrollPane: ElementRef;

  public editorInitSetting;
  public helperName: string;
  public editorContent = { text: '' };
  public discussionStateSubscription: Subscription;
  public discussionMessagesSubscription: Subscription;
  public discussionRequestState = DiscussionRequestStates.NOT_SET;
  public discussionStates = DiscussionRequestStates;
  public assignee;
  public editorInstance;
  public operatorType = DiscussionOperatorTypes;
  public initRequestInProgress = false;
  public errorMessages = new EditorMessages();
  public hasChatDiscussionAcd: boolean = false;
  public hasActiveDiscussion;

  private static readonly InnerHtmlPipe = new InnerHtmlPipe();
  private static readonly DomParser = new DOMParser();

  private _discussionId: string;
  private _linkTargetBlankFilter;
  private _insertActiveDiscussionSubscription: Subscription;
  private _updateActiveDiscussionSubscription: Subscription;
  private _newDiscussionMessageInsertedSubscription: Subscription;
  private _workItemTransferredSubscription: Subscription;
  private _isAttached: boolean;

  constructor(private _bcAnalytics: BcAnalytics,
              private _laneManager: LaneManagerService,
              private _translateService: TranslateService,
              private _operatorsService: OperatorsService,
              private _discussionsMessagesService: DiscussionsMessagesService,
              private _bcRestApi: BcRestApi,
              private _rxEmitter: RxEmitter,
              private _dynamicQuery: DynamicQueryAdapterService,
              private _dynamicQueryDecorator: DynamicQueryDecoratorService,
              private _changeDetectorRef: ChangeDetectorRef,
              private _workItemStateHandler: WorkItemStateHandler,
              private _bcSettingsService: BcSettingsService) {
    this.editorInitSetting = { plugins: 'lists advlist link autolink placeholder insertImage' };
    this._linkTargetBlankFilter = LinkTargetBlankFilter();
  }

  async ngOnInit() {
    if (this._isAgentHelper()) {
      this._discussionId = this.helperDiscussionId;
      this.discussionRequestState = this.discussionStates.HELPING;
    } else {
      this.discussionRequestState = this.discussionStates.NOT_SET;

      this.discussionStateSubscription = this._rxEmitter.subscribe(MessageBusEvents.DiscussionStateChanged + `-${this.workItemId}`, (state) => {
        if (this.discussionRequestState === DiscussionRequestStates.NOT_SET) {
          this._clearEditor();
        }

        this.discussionRequestState = state;
        this._handleScrolling();
      });

      const activeDiscussionObservable = this._dynamicQueryDecorator.create(
        this._dynamicQuery.getQuery('getDiscussionForWorkItem', { WorkItemID: this.workItemId }),
        item => item
      );

      this._insertActiveDiscussionSubscription = activeDiscussionObservable.inserted()
        .subscribe(dynamicQueryEvent => this._activeDiscussionInserted(dynamicQueryEvent.data));

      this._updateActiveDiscussionSubscription = activeDiscussionObservable.updated()
        .subscribe(dynamicQueryEvent => this._activeDiscussionUpdated(dynamicQueryEvent.data));

      await this._loadDiscussionSetting();
    }
    this.discussionMessagesSubscription = this._discussionsMessagesService.initSubscription(this.workItemId);

    this._newDiscussionMessageInsertedSubscription = this._rxEmitter.subscribe(MessageBusEvents.DiscussionMessageInserted + `-${this.workItemId}`, () => {
      this._handleScrolling();
    });

    this._workItemTransferredSubscription = this._rxEmitter.subscribe(MessageBusEvents.WorkItemTransferred + `-${this.workItemId}`, () => {
      if (this._discussionId) {
        this._endDiscussion(this._discussionId);
      }
    });

    this.hasActiveDiscussion = false;
  }

  ngOnDestroy() {
    if (this.discussionStateSubscription) {
      this.discussionStateSubscription.unsubscribe();
      this.discussionStateSubscription = null;
    }
    if (this.discussionMessagesSubscription) {
      this.discussionMessagesSubscription.unsubscribe();
      this.discussionMessagesSubscription = null;
    }
    if (this._insertActiveDiscussionSubscription) {
      this._insertActiveDiscussionSubscription.unsubscribe();
      this._insertActiveDiscussionSubscription = null;
    }
    if (this._updateActiveDiscussionSubscription) {
      this._updateActiveDiscussionSubscription.unsubscribe();
      this._updateActiveDiscussionSubscription = null;
    }
    if (this._newDiscussionMessageInsertedSubscription) {
      this._newDiscussionMessageInsertedSubscription.unsubscribe();
      this._newDiscussionMessageInsertedSubscription = null;
    }
    if (this._workItemTransferredSubscription) {
      this._workItemTransferredSubscription.unsubscribe();
      this._workItemTransferredSubscription = null;
    }
  }

  public onEditorSetupDone(editor) {
    this.editorInstance = editor;

    this.editorInstance.on('init', () => {
      this._updateEditorPlaceholder();
      this._scrollToBottom();
    });
  }

  public onSendRequestClick(): Promise<any> {
    if (this.assignee.id && this.workItemId && !this.hasActiveDiscussion && !this.initRequestInProgress) {
      const initMessage = this.editorInstance.getContent();
      this._bcAnalytics.eventTrack('SendDiscussionRequest', { assigneeType: this.assignee.selectedItemType});
      this.initRequestInProgress = true;
      return this._sendDiscussionRequest(this.workItemId, this.assignee.id, this.assignee.selectedItemType)
        .then((response) => {
          this.initRequestInProgress = false;
          this._discussionId = response.discussionId;
          if (this._discussionId) {
            this.hasActiveDiscussion = true;
          }
          if (this._isMessageNotEmpty(initMessage)) {
            this._sendMessageToDiscussion(this._discussionId, initMessage);
            this.editorContent.text = '';
            this._clearEditor();
          }
          this._changeDetectorRef.detectChanges();
        }).catch((error) => {
          this.hasActiveDiscussion = false;
          this.initRequestInProgress = false;
          if (error.data && error.data.length > 0) {
            this._onDiscussionRequestError(error.data[0]);
          }
          this._changeDetectorRef.detectChanges();
        });
    }
  }

  public closeDiscussionPanel(): void {
    this._laneManager.closeLane(this.workItemId, WorkItemLaneType.Discussions.toString(), this.workItemType);
  }

  public onCancelDiscussionClick(): Promise<any> {
    this._resetHelperInfo();
    this.hasActiveDiscussion = false;
    this._bcAnalytics.eventTrack('CancelDiscussionRequest');
    this.clearErrorMessage();
    return this._cancelDiscussion(this._discussionId);
  }

  public onStartNewDiscussionClick(): void {
    this.hasActiveDiscussion = false;
    this._bcAnalytics.eventTrack('StartNewDiscussion');
    this._resetHelperInfo();
    this.discussionRequestState = DiscussionRequestStates.NOT_SET;
  }

  public onEndDiscussionClick(): Promise<any> {
    this.hasActiveDiscussion = false;
    this._bcAnalytics.eventTrack('DiscussionEnded', { by: this._isAgentHelper() ? 'helper' : 'operator'});
    this.clearErrorMessage();
    return this._endDiscussion(this._discussionId);
  }

  public async onTransferClick(): Promise<void> {
    await this._endDiscussion(this._discussionId);
    this._operatorsService.assignWorkItemFromDiscussion(this.workItemId, this.workItemType, this.assignee.id, this.assignee.selectedItemType, this.assignee.departmentId);
    this._bcAnalytics.eventTrack('DiscussionTransferred');
  }

  public isDiscussionRequestNotSet(): boolean {
    return this.discussionRequestState === DiscussionRequestStates.NOT_SET;
  }

  public setSelectedOperator(selectedHelper): void {
    if (selectedHelper) {
      this.assignee = selectedHelper;
    } else {
      this._resetHelperInfo();
    }
  }

  public onEditorSubmit(editorContent): void {
    let message = this._preProcessMessage(editorContent);
    if (this._isMessageNotEmpty(message)) {
      this._sendMessageToDiscussion(this._discussionId, message);
      this._clearEditor();
    }
  }

  public isFocused(): boolean {
    const focusedWorkItem = this._workItemStateHandler.getFocusedWorkItem();
    if (this._isAgentHelper()) {
      return focusedWorkItem && focusedWorkItem.id === this._discussionId;
    }
    return focusedWorkItem && focusedWorkItem.id === this.workItemId;
  }

  public clearErrorMessage(): void {
    this.errorMessages.clearAllMessages();
  }

  public isChat(): boolean {
    return this.workItemType === WorkItemTypes.CHAT;
  }

  get editorAutoSaveStorageKey() {
    return `discussions-chat-${this.workItemId}`;
  }

  private _onDiscussionRequestError(error): void {
    if (error && error.code === DiscussionErrors.NO_ACD_SETTING) {
      this.errorMessages.addEditorMessage('discussionPermissionError', 'discussions.errorMessageNoAcd', null);
    } else {
      this.errorMessages.addEditorMessage('discussionPermissionError', 'discussions.errorMessage', null);
    }
  }

  private _updateEditorPlaceholder() {
    if (!this.editorInstance) {
      return;
    }
    if (this._isAgentHelper()) {
      this.editorInstance.settings.placeholder = this._translateService.instant('discussions.editorPlaceholderForHelper');
    } else if (this.isDiscussionRequestNotSet()) {
      this.editorInstance.settings.placeholder = this._translateService.instant('discussions.editorPlaceholder');
    } else {
      this.editorInstance.settings.placeholder = this._translateService.instant('discussions.editorPlaceholderInDiscussion', { helperName: this.helperName });
    }
  }

  private _clearEditor(): void {
    if (this.editorInstance) {
      this.editorInstance.setContent('');
    }
  }

  private _preProcessMessage(message): string {
    const anchorTargetBlankAdded = this._addTargetToAnchors(message);
    const cleanMarkupEnrichedWithPushLinks = this._changePushLinkNodes(anchorTargetBlankAdded);
    return cleanMarkupEnrichedWithPushLinks;
  }

  private _changePushLinkNodes(message): string {
    return DiscussionsComponent.InnerHtmlPipe.transform(message).replace(/\[(https?:\/\/[^\]]*)\]/g, '<push>$1</push>');
  }

  private _addTargetToAnchors(message): string {
    if (!message) {
      return message;
    }
    return this._linkTargetBlankFilter(message);
  }

  private _isMessageNotEmpty(message): boolean {
    const replacedMessage = message.replace(/<(?:.|\n)*?>/gm, '').trim();
    const doc = DiscussionsComponent.DomParser.parseFromString(message, 'text/html');
    const imageElement: HTMLImageElement = doc.querySelector('img');
    return !!imageElement || replacedMessage !== '';
  }

  private _isAgentHelper(): boolean {
    return this.workItemType === WorkItemTypes.DISCUSSION;
  }

  private _sendDiscussionRequest(workItemId: string, helperId: string, selectedItemType: string): Promise<InitDiscussionResponseModel> {
    const workItemTypeValue = WorkItemHelper.convertItemTypeToDbEnum(this.workItemType);
    return this._bcRestApi.initDiscussion(workItemId, helperId, workItemTypeValue, selectedItemType);
  }

  private _sendMessageToDiscussion(discussionId: string, message): Promise<void> {
    return this._bcRestApi.sendDiscussionMessage(discussionId, message);
  }

  private _cancelDiscussion(discussionId: string): Promise<void> {
    return this._bcRestApi.cancelDiscussion(discussionId);
  }

  private _endDiscussion(discussionId: string): Promise<void> {
    return this._bcRestApi.endDiscussion(discussionId);
  }

  private _resetHelperInfo(): void {
    this.helperName = null;
    this.assignee = null;
  }

  private _activeDiscussionInserted(activeDiscussion: DiscussionItem): void {
    if (!activeDiscussion.Ended) {
      this._discussionId = activeDiscussion.DiscussionID;
      this.assignee = {
        id: activeDiscussion.HelperID,
        selectedItemType: AssigneeTypes.OPERATOR,
        departmentId: activeDiscussion.DepartmentID
      };
      this.helperName = activeDiscussion.HelperName;
      this._updateEditorPlaceholder();
    }
  }

  private _activeDiscussionUpdated(activeDiscussion: DiscussionItem): void {
    if (!activeDiscussion.Ended) {
      this._discussionId = activeDiscussion.DiscussionID;
      this.assignee = {
        id: activeDiscussion.HelperID,
        selectedItemType: AssigneeTypes.OPERATOR,
        departmentId: activeDiscussion.DepartmentID
      };
      this.helperName = activeDiscussion.HelperName;
      this._updateEditorPlaceholder();
    }
  }

  private _setIsAttachedToBottom(): void {
    if (!this.scrollPane) {
      this._isAttached = true;
    } else {
      const scrollEl = this.scrollPane.nativeElement;
      this._isAttached = scrollEl.scrollTop + scrollEl.clientHeight + 1 >= scrollEl.scrollHeight;
    }
  }

  private _scrollToBottom(): void {
    if (this.scrollPane && this._isAttached) {
      this.scrollPane.nativeElement.scrollTop = this.scrollPane.nativeElement.scrollHeight;
    }
  }

  private _handleScrolling(): void {
    this._setIsAttachedToBottom();
    this._changeDetectorRef.detectChanges();
    this._scrollToBottom();
  }

  private async _loadDiscussionSetting(): Promise<void> {
    const settings: Settings = await this._bcSettingsService.getSettings();
    this.hasChatDiscussionAcd = this._bcSettingsService.ChatFeatures.isEnabled(settings, BcFeatures.LC_DISCUSSIONS_ACD);
    this._changeDetectorRef.detectChanges();
  }
}
